import { useMemo, useState } from "react";
import { Collapse, Table } from "react-bootstrap";
import Button from "react-bootstrap/Button";
import {
  fetchActionsUtil,
  formatDate,
  formatForQty,
} from "../../../utils/helpers";
import currency from "currency.js";
import {
  addDays,
  addMonths,
  addQuarters,
  addYears,
  differenceInCalendarDays,
  endOfDay,
  format,
  getDate,
  getDaysInMonth,
  startOfDay,
} from "date-fns";
import { useQuery } from "react-query";
import { useAuth } from "../../../hooks/useAuth";
import queryString from "query-string";
import { ExcelIcon } from "../../Icons";
import NoTableItem from "../../utils/NoTableItem";
import CachedIcon from "mdi-react/CachedIcon";
import * as XLSX from "xlsx";

const EPSILON = 0.01;
const MAX_PROJECTION_ITERATIONS_FAILSAFE = 5000;

const getResponseBarCode = itemData =>
  itemData?.Bar_Code ?? itemData?.bar_code ?? itemData?.BarCode ?? "";

const normalizeItem = item => ({
  ...item,
  Bar_Code: item?.Bar_Code ?? item?.bar_code ?? item?.BarCode ?? "",
  Warehouse: item?.Warehouse ?? item?.W_ID ?? "",
  Item_Name: item?.Item_Name ?? item?.item_name ?? item?.ItemName ?? "",
  UnitCost: Number(item?.UnitCost ?? item?.Unit_Cost ?? item?.unitCost ?? 0),
  Quantity: Number(item?.Quantity ?? item?.Qty ?? item?.quantity ?? 0),
});

const getValidDate = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getProratedAmountForRange = ({
  amount,
  periodStart,
  periodEnd,
  rangeStart,
  rangeEnd,
}) => {
  const normalizedPeriodStart = startOfDay(periodStart);
  const normalizedPeriodEnd = endOfDay(periodEnd);
  const normalizedRangeStart = rangeStart ? startOfDay(rangeStart) : null;
  const normalizedRangeEnd = rangeEnd ? endOfDay(rangeEnd) : null;

  const shouldProrateStart =
    normalizedRangeStart && normalizedRangeStart > normalizedPeriodStart;
  const shouldProrateEnd =
    normalizedRangeEnd && normalizedRangeEnd < normalizedPeriodEnd;

  if (normalizedRangeStart && normalizedPeriodEnd < normalizedRangeStart) {
    return 0;
  }

  if (normalizedRangeEnd && normalizedPeriodStart > normalizedRangeEnd) {
    return 0;
  }

  const effectiveStart = normalizedRangeStart
    ? shouldProrateStart
      ? normalizedRangeStart > normalizedPeriodStart
        ? normalizedRangeStart
        : normalizedPeriodStart
      : normalizedPeriodStart
    : normalizedPeriodStart;

  const effectiveEnd = normalizedRangeEnd
    ? shouldProrateEnd
      ? normalizedRangeEnd < normalizedPeriodEnd
        ? normalizedRangeEnd
        : normalizedPeriodEnd
      : normalizedPeriodEnd
    : normalizedPeriodEnd;

  if (effectiveStart > effectiveEnd) {
    return 0;
  }

  const totalDays =
    differenceInCalendarDays(normalizedPeriodEnd, normalizedPeriodStart) + 1;
  const overlapDays =
    differenceInCalendarDays(effectiveEnd, effectiveStart) + 1;

  if (totalDays <= 0 || overlapDays <= 0) {
    return 0;
  }

  return currency(amount)
    .multiply(overlapDays)
    .divide(totalDays).value;
};

const calculateProjections = ({ item, itemData, startDate, endDate }) => {
  if (!itemData?.depreciation || !item?.UnitCost) {
    return [];
  }

  const {
    percentage = 0,
    cycle,
    Start_Date = new Date(),
  } = itemData.depreciation;

  const openingValue = currency(item.UnitCost)
    .multiply(item.Quantity)
    .subtract(itemData?.amountDepreciationBeforeTheStartDate || 0).value;

  const depreciationRate = percentage / 100;
  const startDateObj = new Date(Start_Date);
  const rangeStart = getValidDate(startDate);
  const rangeEnd = getValidDate(endDate);

  if (rangeStart && rangeEnd && startOfDay(rangeStart) > endOfDay(rangeEnd)) {
    return [];
  }

  let projections = [];
  let currentValue = openingValue;
  let serialNumber = 1;
  let previousPeriodEnd = addDays(startDateObj, -1);
  let currentDate = new Date(
    startDateObj.getFullYear(),
    startDateObj.getMonth() + 1,
    0
  );

  let unchangedIterations = 0;

  while (
    currentValue > EPSILON &&
    serialNumber <= MAX_PROJECTION_ITERATIONS_FAILSAFE
  ) {
    let depreciationAmount;

    if (itemData.depreciation.type === "Percentage") {
      depreciationAmount = openingValue * depreciationRate;
    } else {
      depreciationAmount = parseFloat(itemData.depreciation.fixedAmount || 0);
    }

    if (!Number.isFinite(depreciationAmount)) {
      break;
    }

    if (serialNumber === 1) {
      const daysInMonth = getDaysInMonth(startDateObj);
      const daysUsed = daysInMonth - getDate(startDateObj) + 1;

      depreciationAmount = currency(depreciationAmount)
        .multiply(daysUsed)
        .divide(daysInMonth).value;
    }

    if (!Number.isFinite(depreciationAmount)) {
      break;
    }

    depreciationAmount = Math.min(depreciationAmount, currentValue);

    if (depreciationAmount <= EPSILON) {
      break;
    }

    const periodStart =
      serialNumber === 1 ? startDateObj : addDays(previousPeriodEnd, 1);
    const periodEnd = currentDate;

    projections.push({
      sn: serialNumber,
      date: format(currentDate, "dd MMM yyyy"),
      amount: depreciationAmount,
      remainingValue: currentValue - depreciationAmount,
      periodStart,
      periodEnd,
    });

    const nextValue = currentValue - depreciationAmount;

    if (!Number.isFinite(nextValue)) {
      break;
    }

    if (Math.abs(nextValue - currentValue) < 0.000001) {
      unchangedIterations += 1;
      if (unchangedIterations >= 2) {
        break;
      }
    } else {
      unchangedIterations = 0;
    }

    currentValue = nextValue;
    previousPeriodEnd = currentDate;

    switch (cycle) {
      case "Monthly":
        currentDate = addMonths(currentDate, 1);
        currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
        break;
      case "Quaterly": {
        currentDate = addQuarters(currentDate, 1);
        const quarterEndMonth =
          Math.ceil((currentDate.getMonth() + 1) / 3) * 3 - 1;
        currentDate = new Date(
          currentDate.getFullYear(),
          quarterEndMonth + 1,
          0
        );
        break;
      }
      case "Annually":
        currentDate = addYears(currentDate, 1);
        currentDate = new Date(currentDate.getFullYear(), 11, 31);
        break;
      default:
        currentDate = addMonths(currentDate, 1);
        currentDate = new Date(
          currentDate.getFullYear(),
          currentDate.getMonth() + 1,
          0
        );
    }

    serialNumber += 1;
  }

  if (!rangeStart && !rangeEnd) {
    return projections.map(
      ({ periodStart, periodEnd, ...projection }) => projection
    );
  }

  let depreciationBeforeRange = 0;

  if (rangeStart) {
    const beforeRangeEnd = addDays(startOfDay(rangeStart), -1);

    projections.forEach(projection => {
      if (startOfDay(projection.periodStart) > beforeRangeEnd) {
        return;
      }

      depreciationBeforeRange += getProratedAmountForRange({
        amount: projection.amount,
        periodStart: projection.periodStart,
        periodEnd: projection.periodEnd,
        rangeStart: null,
        rangeEnd: beforeRangeEnd,
      });
    });
  }

  let runningValue = Math.max(openingValue - depreciationBeforeRange, 0);
  const rangedProjections = [];

  projections.forEach(projection => {
    const rangedAmount = getProratedAmountForRange({
      amount: projection.amount,
      periodStart: projection.periodStart,
      periodEnd: projection.periodEnd,
      rangeStart,
      rangeEnd,
    });

    if (rangedAmount <= 0) {
      return;
    }

    const safeAmount = Math.min(rangedAmount, runningValue);
    runningValue = Math.max(runningValue - safeAmount, 0);

    rangedProjections.push({
      sn: rangedProjections.length + 1,
      date: projection.date,
      amount: safeAmount,
      remainingValue: runningValue,
    });
  });

  return rangedProjections;
};

// can take the jobnumber to get assets issued to job

// can take list of items with barcodes to get the projection for each item

export default function ProjectedDepreciationTable({
  items = [],
  Bar_Code,
  Warehouse,
  Item_Name,
  UnitCost,
  Quantity,
  jobNumber,
  contracts,

  //
  startDate,
  endDate,
}) {
  const { backendUrl } = useAuth();
  const [openTables, setOpenTables] = useState({});
  const [projectionPages, setProjectionPages] = useState({});
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const {
    data: jobIssueItems = [],
    error: jobItemsError,
    refetch: refetchJobItems,
  } = useQuery(
    ["JOB_ISSUE_ITEMS_FOR_DEPRECIATION", jobNumber, contracts],
    async () => {
      const response = await fetchActionsUtil(
        `${backendUrl}/api/warehouse/job-issue-items?&${queryString.stringify({
          page: 1,
          limit: 1000,
          contracts: contracts || jobNumber,
        })}`,
        "GET"
      );

      return response?.data?.issuedItems || [];
    },
    {
      enabled: Boolean(jobNumber),
    }
  );

  const normalizedItems = useMemo(() => {
    if (jobNumber) {
      return jobIssueItems.map(normalizeItem).filter(item => item.Bar_Code);
    }

    if (Array.isArray(items) && items.length > 0) {
      return items.map(normalizeItem).filter(item => item.Bar_Code);
    }

    if (!Bar_Code) {
      return [];
    }

    return [
      normalizeItem({
        Bar_Code,
        Warehouse,
        Item_Name,
        UnitCost,
        Quantity,
      }),
    ];
  }, [
    Bar_Code,
    Item_Name,
    Quantity,
    UnitCost,
    Warehouse,
    items,
    jobIssueItems,
    jobNumber,
  ]);

  const barCodes = useMemo(
    () =>
      [
        ...new Set(normalizedItems.map(item => item.Bar_Code).filter(Boolean)),
      ].join(","),
    [normalizedItems]
  );

  const getBatches = async () => {
    let response = await fetch(
      `${backendUrl}/api/warehouse/asset-depreciation-projection-multiple-items?${queryString.stringify(
        { Bar_Codes: barCodes }
      )}`,
      {
        method: "GET",
        headers: {
          Accept: "Application/json",
          "Content-Type": "Application/json",
        },
        credentials: "include",
      }
    );
    if (!response.ok) {
      response = await response.json();
      throw new Error(response.message);
    }
    const { data } = await response.json();
    return Array.isArray(data) ? data : [];
  };

  const { data = [], error, isFetching, isLoading, refetch } = useQuery(
    ["DEPRECIATION_DATA_MULTIPLE", barCodes],
    getBatches,
    {
      enabled: Boolean(barCodes),
    }
  );

  const activeError = jobItemsError || error;

  const depreciationDataByBarCode = useMemo(() => {
    const responseMap = new Map();

    data.forEach((itemData, index) => {
      const itemBarCode = getResponseBarCode(itemData);

      if (itemBarCode) {
        // we need some data from normalized items to calculate the projection so we set the data with barcode as key and if barcode is not available we set with index as key
        const foundItem = normalizedItems.find(
          item => item.Bar_Code === itemBarCode
        );
        const combinedData = {
          ...itemData,

          // FIELDS FOR CALCULATION
          Quantity: foundItem?.Quantity ?? itemData.Quantity ?? 0,
        };

        responseMap.set(String(itemBarCode), combinedData);
      }

      responseMap.set(`__index_${index}`, itemData);
    });
    console.log("depreciation data by barcode", responseMap);
    return responseMap;
  }, [data]);

  const projectionRows = useMemo(
    () =>
      normalizedItems.map((item, index) => {
        let itemData =
          depreciationDataByBarCode.get(String(item.Bar_Code)) ??
          depreciationDataByBarCode.get(`__index_${index}`) ??
          null;

        console.log("item data for projection", { item, itemData });

        itemData = {
          ...itemData,
          depreciation: itemData?.depreciation.depreciation,
        };

        return {
          item,
          itemData,
          projections: calculateProjections({
            item,
            itemData,
            startDate,
            endDate,
          }),
        };
      }),
    [depreciationDataByBarCode, normalizedItems, startDate, endDate]
  );

  const totalPeriods = useMemo(
    () => projectionRows.reduce((sum, row) => sum + row.projections.length, 0),
    [projectionRows]
  );

  const exportToExcel = () => {
    const excelData = [
      [
        "Item Code",
        "Item Name",
        "Warehouse",
        "S/N",
        "Date",
        "Amount",
        "Net Book Value",
      ],
      ...projectionRows.flatMap(({ item, projections }) => {
        if (!projections.length) {
          return [
            [item.Bar_Code, item.Item_Name, item.Warehouse, "", "", "", ""],
          ];
        }

        return projections.map(projection => [
          item.Bar_Code,
          item.Item_Name,
          item.Warehouse,
          projection.sn,
          projection.date,
          projection.amount,
          projection.remainingValue,
        ]);
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Projected Depreciation");

    const filename = `Projected_Depreciation_${
      new Date().toISOString().split("T")[0]
    }.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  return (
    <div className="projected-depreciation-container p-2 p-md-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h5 className="mb-1">Projected Depreciation</h5>
          {/* <small className="text-muted">
            {projectionRows.length} item(s) | {totalPeriods} period(s)
          </small> */}
        </div>
        <div className="d-flex gap-2">
          <Button
            onClick={() => {
              if (jobNumber) {
                refetchJobItems();
              }
              refetch();
            }}
            variant=""
            className="text-primary"
            disabled={(!barCodes && !jobNumber) || isFetching}
          >
            <CachedIcon />
          </Button>
          <Button
            variant=""
            title="Export"
            className="border"
            onClick={exportToExcel}
            disabled={!projectionRows.length}
          >
            <ExcelIcon color={"green"} /> Export to Excel
          </Button>
        </div>
      </div>

      {!normalizedItems.length ? (
        <NoTableItem />
      ) : (
        <div className="d-flex flex-column gap-2">
          {projectionRows.map(({ item, itemData, projections }, index) => {
            const rowKey = `${item.Bar_Code}-${index}`;
            const isTableOpen = Boolean(openTables[rowKey]);
            const currentValue = currency(item.UnitCost || 0, {
              symbol: "",
            })
              .multiply(item.Quantity || 0)
              .subtract(itemData?.depreciatedValue || 0)
              .format();

            return (
              <div key={rowKey} className="bg-white p-0">
                <Table responsive borderless size="sm" className="small mb-2">
                  <tbody>
                    <tr>
                      <th>Item Code</th>
                      <td>{item.Bar_Code}</td>
                      <th>Item Name</th>
                      <td>{item.Item_Name}</td>
                    </tr>
                    <tr>
                      <th>Qty</th>
                      <td>
                        {currency(item.Quantity, {
                          symbol: "",
                          format: formatForQty,
                        }).format()}
                      </td>
                      <th>Cost</th>
                      <td>
                        {currency(item.UnitCost, { symbol: "" }).format()}
                      </td>
                    </tr>
                    <tr>
                      <th>Cycle</th>
                      <td>{itemData?.depreciation?.cycle || "..."}</td>
                      <th>Type</th>
                      <td>{itemData?.depreciation?.type || "..."}</td>
                    </tr>
                    <tr>
                      <th>Post Date</th>
                      <td>
                        {itemData?.depreciation
                          ? formatDate(itemData.depreciation.Post_Date)
                          : "..."}
                      </td>
                      <th>Start Date</th>
                      <td>
                        {itemData?.depreciation
                          ? formatDate(itemData.depreciation.Start_Date)
                          : "..."}
                      </td>
                    </tr>
                    <tr>
                      <th>Total Unit Cost</th>
                      <td>
                        {currency(item.UnitCost, {
                          symbol: "",
                        })
                          .multiply(item.Quantity)
                          .format()}
                      </td>
                      <th>Total Depreciated</th>
                      <td>
                        {currency(itemData?.depreciatedValue || 0, {
                          symbol: "",
                        }).format()}
                      </td>
                    </tr>
                    <tr>
                      <th>Current Value</th>
                      <td>{currentValue}</td>
                      <th>Periods</th>
                      <td>{projections.length}</td>
                    </tr>
                    <tr>
                      <th>Projected End</th>
                      <td>
                        {projections.length > 0
                          ? projections[projections.length - 1].date
                          : "N/A"}
                      </td>
                      <th>
                        {itemData?.depreciation?.type === "Percentage"
                          ? "Percentage"
                          : "Fixed Amount"}
                      </th>
                      <td>
                        {itemData?.depreciation?.type === "Percentage"
                          ? `${itemData?.depreciation?.percentage ?? "..."}%`
                          : itemData?.depreciation?.fixedAmount ?? "..."}
                      </td>
                    </tr>
                  </tbody>
                </Table>

                {activeError && index === 0 ? (
                  <p className="text-danger small mb-2">
                    {activeError.message}
                  </p>
                ) : null}

                <div className="d-flex justify-content-end mb-2">
                  <div className="d-flex align-items-center gap-2 me-2">
                    <small className="text-muted">Rows:</small>
                    <select
                      className="form-select form-select-sm"
                      value={rowsPerPage}
                      onChange={e => {
                        const nextRows = Number(e.target.value);
                        setRowsPerPage(nextRows);
                        setProjectionPages({});
                      }}
                      style={{ width: 84 }}
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                  <Button
                    size="sm"
                    variant="light-blue"
                    onClick={() =>
                      setOpenTables(prev => ({
                        ...prev,
                        [rowKey]: !prev[rowKey],
                      }))
                    }
                  >
                    {isTableOpen ? "Hide Projections" : "Show Projections"}
                  </Button>
                </div>

                <Collapse in={isTableOpen}>
                  <div>
                    {isLoading && !itemData ? (
                      <div className="small text-muted">Loading...</div>
                    ) : projections.length > 0 ? (
                      (() => {
                        const totalPages = Math.max(
                          1,
                          Math.ceil(projections.length / rowsPerPage)
                        );
                        const currentPage = Math.min(
                          projectionPages[rowKey] || 1,
                          totalPages
                        );
                        const startIndex = (currentPage - 1) * rowsPerPage;
                        const paginatedProjections = projections.slice(
                          startIndex,
                          startIndex + rowsPerPage
                        );

                        return (
                          <>
                            <Table
                              responsive
                              bordered
                              striped
                              size="sm"
                              className="mb-2"
                            >
                              <thead>
                                <tr>
                                  <th>S/N</th>
                                  <th>Date</th>
                                  <th>Amount</th>
                                  <th>Net Book Value</th>
                                </tr>
                              </thead>
                              <tbody>
                                {paginatedProjections.map(projection => (
                                  <tr key={projection.sn}>
                                    <td>{projection.sn}</td>
                                    <td>{projection.date}</td>
                                    <td>
                                      {currency(projection.amount, {
                                        symbol: "",
                                      }).format()}
                                    </td>
                                    <td>
                                      {currency(projection.remainingValue, {
                                        symbol: "",
                                      }).format()}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>

                            {totalPages > 1 ? (
                              <div className="d-flex justify-content-between align-items-center small">
                                <span className="text-muted">
                                  Page {currentPage} of {totalPages}
                                </span>
                                <div className="d-flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={currentPage <= 1}
                                    onClick={() =>
                                      setProjectionPages(prev => ({
                                        ...prev,
                                        [rowKey]: Math.max(
                                          1,
                                          (prev[rowKey] || 1) - 1
                                        ),
                                      }))
                                    }
                                  >
                                    Previous
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    disabled={currentPage >= totalPages}
                                    onClick={() =>
                                      setProjectionPages(prev => ({
                                        ...prev,
                                        [rowKey]: Math.min(
                                          totalPages,
                                          (prev[rowKey] || 1) + 1
                                        ),
                                      }))
                                    }
                                  >
                                    Next
                                  </Button>
                                </div>
                              </div>
                            ) : null}
                          </>
                        );
                      })()
                    ) : (
                      <NoTableItem />
                    )}
                  </div>
                </Collapse>

                <hr className="my-5" />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
